/**
 * @fileoverview Draft reading pane for the Drafts folder (MOR-529).
 *
 * Matches the thread reader's visual language: recipient header (avatar,
 * resolved contact name → opens the contact panel, role tag chips, status
 * badge, actions), serif subject, Morgan's-note callout, and the message
 * body. Pending drafts are editable in place (recipient chips + body) with
 * Approve & send / Reject, plus "Ask Morgan to revise" — the agent's rewrite
 * lands as a line diff. Decided drafts render read-only; the audit trail
 * sits behind a history toggle.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { diffLines } from "diff";
import { useMutation, useQuery } from "convex/react";
import { formatDistanceToNow } from "date-fns";
import { FileText, History, Paperclip, Send, Sparkles, X } from "lucide-react";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { Spinner } from "@/ui/primitives/spinner";
import { Textarea } from "@/ui/primitives/textarea";
import { NotesEditor } from "@/ui/composed/editor/notes-editor";
import { ROLE_META, type ContactRole } from "@/lib/contact-roles";
import { cn } from "@/utils/misc";
import { extractErrorMessage } from "@/utils/errors";
import { ApprovalAuditLog } from "./ApprovalAuditLog";
import { avatarTint, initialsFor } from "./avatar";
import { draftStatusMeta } from "./draftStatus";
import {
  AttachmentPreviewModal,
  type AttachmentPreview,
} from "./AttachmentPreviewModal";
import { RejectDraftDialog, ReviseDraftDialog } from "./DraftActionDialogs";
import { splitQuotedText } from "./quotedText";
import { RecipientInput, type RecipientOption } from "./RecipientInput";
import { htmlToPlainText, plainTextToHtml } from "./htmlBody";
import { DocumentDetail } from "@/components/jobs/phases/gather/documents/DocumentDetail";
import { FilePreviewDialog, type FilePreview } from "./FilePreviewDialog";
import { MailAttachmentPicker } from "./MailAttachmentPicker";
import { ComposeSignaturePreview } from "./ComposeSignaturePreview";
import type { ComposerAttachment, MailDraft } from "./types";

const SOURCE_LABEL: Record<MailDraft["source"], string> = {
  agent_reply: "Reply draft by Morgan",
  agent_chat: "Drafted by Morgan (chat)",
  day_one: "Day One send",
  pod_adjudication: "POD adjudication notice",
  user: "Written by you",
};

interface JobContactLite {
  _id: Id<"jobContacts">;
  displayName: string;
  email?: string | null;
  effectiveEmail?: string | null;
  roles?: string[] | null;
}

export function DraftReader({
  jobId,
  draftId,
  onOpenContact,
}: {
  jobId: Id<"jobs">;
  draftId: Id<"mailDrafts">;
  onOpenContact?: (contactId: Id<"jobContacts">) => void;
}) {
  const drafts = useQuery(api.mail.drafts.listDrafts, { jobId });
  const jobContacts = useQuery(api.contacts.queries.listJobContacts, {
    jobId,
  });
  const editDraft = useMutation(api.mail.drafts.editDraft);
  const approveDraft = useMutation(api.mail.drafts.approveDraft);
  const rejectDraft = useMutation(api.mail.drafts.rejectDraft);
  const requestDraftRevision = useMutation(
    api.jobs.summarization.agents.mailAgent.publicMutations
      .requestDraftRevision,
  );

  const draft = drafts?.find((d) => d._id === draftId) ?? null;
  const pending = draft?.status === "pending";

  // The conversation this draft replies to — shown above the draft so the
  // reviewer has the thread context in place.
  const threadData = useQuery(
    api.mail.queries.getThreadWithMessages,
    draft?.threadId ? { threadId: draft.threadId } : "skip",
  );

  // Preview info for the draft's attachments: signed URL + matching job
  // document (resolved by storageId so older drafts work too).
  const attachmentPreviewInfo = useQuery(
    api.mail.queries.getAttachmentPreviewInfo,
    draft?.attachments && draft.attachments.length > 0
      ? {
          jobId,
          storageIds: draft.attachments.map((a) => a.storageId),
        }
      : "skip",
  ) as
    | Array<{
        storageId: Id<"_storage">;
        url: string | null;
        documentId: Id<"documents"> | null;
      }>
    | undefined;
  const infoByStorageId = useMemo(() => {
    const map = new Map<
      string,
      { url: string | null; documentId: Id<"documents"> | null }
    >();
    for (const info of attachmentPreviewInfo ?? []) {
      map.set(info.storageId, {
        url: info.url,
        documentId: info.documentId,
      });
    }
    return map;
  }, [attachmentPreviewInfo]);

  const [to, setTo] = useState<string[]>([]);
  const [cc, setCc] = useState<string[]>([]);
  const [bcc, setBcc] = useState<string[]>([]);
  const [bccOpen, setBccOpen] = useState(false);
  const showBcc = bccOpen || bcc.length > 0;
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  // Rich-text HTML mirror of `body`. `body` (plain text) stays the canonical
  // value for the dirty check / diff / text part; `bodyHtml` carries the
  // formatted version the composer authors. `baselineHtmlRef` is the seeded
  // HTML so formatting-only edits register as dirty.
  const [bodyHtml, setBodyHtml] = useState("");
  const baselineHtmlRef = useRef<string>("");
  const [draftAttachments, setDraftAttachments] = useState<
    ComposerAttachment[]
  >([]);
  const [working, setWorking] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [preview, setPreview] = useState<AttachmentPreview | null>(null);
  // Full document detail sheet for job-document attachments; type-aware
  // file dialog for everything else.
  const [detailDocumentId, setDetailDocumentId] =
    useState<Id<"documents"> | null>(null);
  const [filePreview, setFilePreview] = useState<FilePreview | null>(null);

  // Revision flow (mirror of the thread composer's).
  const [reviseOpen, setReviseOpen] = useState(false);
  const [revising, setRevising] = useState(false);
  const [previousBody, setPreviousBody] = useState<string | null>(null);
  const [showDiff, setShowDiff] = useState(false);

  // Contact overlay for recipients: name, roles, panel link.
  const contactByEmail = useMemo(() => {
    const map = new Map<string, JobContactLite>();
    for (const contact of jobContacts ?? []) {
      for (const email of [contact.effectiveEmail, contact.email]) {
        if (email) map.set(email.trim().toLowerCase(), contact);
      }
    }
    return map;
  }, [jobContacts]);
  const recipientOptions: RecipientOption[] = useMemo(
    () =>
      (jobContacts ?? [])
        .map((c) => ({
          email: (c.effectiveEmail ?? c.email ?? "").trim(),
          displayName: c.displayName,
        }))
        .filter((c) => c.email.length > 0),
    [jobContacts],
  );
  const primaryContact = draft
    ? contactByEmail.get((draft.to[0] ?? "").trim().toLowerCase())
    : undefined;
  const roleTags = useMemo(() => {
    if (!draft) return [];
    const roles: string[] = [];
    for (const address of [...draft.to, ...draft.cc]) {
      const contact = contactByEmail.get(address.trim().toLowerCase());
      for (const role of contact?.roles ?? []) {
        if (!roles.includes(role)) roles.push(role);
      }
    }
    return roles.slice(0, 3);
  }, [draft, contactByEmail]);

  // Re-seed the form when a different draft (or a status flip) is selected —
  // intentionally NOT on every draft object identity change, or reactive
  // list updates would clobber in-progress edits.
  const seededKey = `${draftId}:${draft?.status ?? "missing"}`;
  const seededRef = useRef<string | null>(null);
  useEffect(() => {
    if (!draft || seededRef.current === seededKey) return;
    seededRef.current = seededKey;
    setTo(draft.to);
    setCc(draft.cc);
    setBcc(draft.bcc ?? []);
    setBccOpen(false);
    setSubject(draft.subject);
    setBody(draft.bodyText);
    const seedHtml = draft.bodyHtml ?? plainTextToHtml(draft.bodyText);
    setBodyHtml(seedHtml);
    baselineHtmlRef.current = seedHtml;
    setDraftAttachments(
      (draft.attachments ?? []).map((a) => ({
        filename: a.filename,
        contentType: a.contentType,
        storageId: a.storageId,
        documentId: a.documentId,
      })),
    );
    setRejectOpen(false);
    setShowAudit(false);
    setReviseOpen(false);
    setRevising(false);
    setShowDiff(false);
    setPreviousBody(null);
  }, [draft, seededKey]);

  // Revision landed → show the diff and re-seed the body.
  useEffect(() => {
    if (!revising || previousBody === null || !draft) return;
    if (draft.bodyText !== previousBody) {
      setRevising(false);
      setShowDiff(true);
      setTo(draft.to);
      setCc(draft.cc);
      setBcc(draft.bcc ?? []);
      setSubject(draft.subject);
      setBody(draft.bodyText);
      // Morgan's revision returns plain text; re-seed the rich editor from it
      // so the composer reflects the new body (dropping any stale formatting).
      const revisedHtml = plainTextToHtml(draft.bodyText);
      setBodyHtml(revisedHtml);
      baselineHtmlRef.current = revisedHtml;
    }
  }, [revising, previousBody, draft]);

  // Fast-revise failure: stop the spinner and surface the error (fixes the
  // old spin-forever behaviour when a revision never landed).
  useEffect(() => {
    if (!revising || !draft) return;
    if (draft.revisionStatus === "failed") {
      setRevising(false);
      setPreviousBody(null);
      toast.error(
        draft.revisionError ?? "Morgan couldn't revise the draft. Try again.",
      );
    }
  }, [revising, draft]);

  if (drafts === undefined) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner className="text-muted-foreground" />
      </div>
    );
  }

  if (!draft) {
    return (
      <div className="flex h-full items-center justify-center px-6 text-center text-sm text-muted-foreground">
        This draft is no longer available.
      </div>
    );
  }

  const badge = draftStatusMeta(draft.status);
  const headerName =
    primaryContact?.displayName ?? draft.to[0] ?? "(no recipients)";
  // Generated-document drafts (Day One) carry a composite bodyHtml (cover +
  // rendered precedent) that a minimal editor can't represent, so they keep
  // the plain-text body flow. Everything else gets the rich-text composer.
  const richBody = !draft.jobPrecedentId;
  const attachmentsDirty =
    JSON.stringify(draftAttachments.map((a) => a.storageId)) !==
    JSON.stringify((draft.attachments ?? []).map((a) => a.storageId));
  const dirty =
    to.join(",") !== draft.to.join(",") ||
    cc.join(",") !== draft.cc.join(",") ||
    bcc.join(",") !== (draft.bcc ?? []).join(",") ||
    subject !== draft.subject ||
    body !== draft.bodyText ||
    (richBody && bodyHtml !== baselineHtmlRef.current) ||
    attachmentsDirty;

  // The composer edits HTML; keep the plain-text mirror (`body`) in sync so
  // it can serve as the text/plain part and feed the revision diff.
  const handleBodyHtmlChange = (html: string) => {
    setBodyHtml(html);
    setBody(htmlToPlainText(html));
  };

  const handleApprove = async () => {
    const toList = to.map((a) => a.trim()).filter(Boolean);
    if (toList.length === 0) {
      toast.error("Add at least one recipient.");
      return;
    }
    setWorking(true);
    try {
      if (dirty) {
        await editDraft({
          draftId: draft._id,
          to: toList,
          cc: cc.map((a) => a.trim()).filter(Boolean),
          bcc: bcc.map((a) => a.trim()).filter(Boolean),
          subject,
          bodyText: body,
          ...(richBody ? { bodyHtml } : {}),
          ...(attachmentsDirty
            ? {
                attachments: draftAttachments.map((a) => ({
                  filename: a.filename,
                  contentType: a.contentType,
                  storageId: a.storageId,
                  documentId: a.documentId,
                })),
              }
            : {}),
        });
      }
      await approveDraft({ draftId: draft._id });
      toast.success("Approved — sending...");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to approve draft"));
    } finally {
      setWorking(false);
    }
  };

  const handleReject = async (reason: string) => {
    setWorking(true);
    try {
      await rejectDraft({
        draftId: draft._id,
        reason: reason.trim() || undefined,
      });
      setRejectOpen(false);
      toast.success("Draft rejected");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to reject draft"));
    } finally {
      setWorking(false);
    }
  };

  const handleRequestRevision = async (instructions: string) => {
    try {
      setPreviousBody(draft.bodyText);
      await requestDraftRevision({
        draftId: draft._id,
        instructions,
      });
      setRevising(true);
      setShowDiff(false);
      setReviseOpen(false);
      toast.success("Morgan is revising the draft...");
    } catch (err) {
      setPreviousBody(null);
      toast.error(extractErrorMessage(err, "Failed to request a revision"));
    }
  };

  return (
    <div className="flex h-full flex-col">
      {/* Recipient header — mirrors the thread reader */}
      <div className="flex items-center justify-between gap-3 border-b px-6 py-4">
        <div className="flex min-w-0 items-center gap-3">
          <div
            className={cn(
              "flex size-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold",
              avatarTint(draft.to[0] ?? headerName),
            )}
          >
            {initialsFor(headerName)}
          </div>
          <div className="min-w-0">
            {primaryContact && onOpenContact ? (
              <button
                type="button"
                onClick={() => onOpenContact(primaryContact._id)}
                className="block truncate text-sm font-semibold text-foreground hover:underline"
              >
                {headerName}
              </button>
            ) : (
              <p className="truncate text-sm font-semibold text-foreground">
                {headerName}
              </p>
            )}
            <p className="truncate text-xs text-muted-foreground">
              {SOURCE_LABEL[draft.source]} ·{" "}
              {formatDistanceToNow(draft._creationTime, { addSuffix: true })}
            </p>
            {roleTags.length > 0 && (
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                {roleTags.map((role) => {
                  const meta = ROLE_META[role as ContactRole];
                  if (!meta) return null;
                  return (
                    <Badge
                      key={role}
                      color={meta.badgeColor}
                      variant="soft"
                      size="sm"
                      className="whitespace-nowrap"
                    >
                      {meta.label}
                    </Badge>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span
            className={cn(
              "rounded-md border px-2 py-1 text-[11px] font-medium leading-none",
              badge.className,
            )}
          >
            {badge.label}
          </span>
          <Button
            size="icon"
            variant="ghost"
            className="size-8"
            onClick={() => setShowAudit((v) => !v)}
            title="Audit trail"
            aria-label="Audit trail"
          >
            <History className="size-4" />
          </Button>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pt-5">
        {(revising || draft.revisionStatus === "running") && (
          // Negative top margin cancels the container's pt-5 so this transient
          // status bar stays flush against the header.
          <div className="-mt-5 mb-5 flex items-center gap-2 border-b bg-primary/5 px-6 py-2.5 text-xs text-primary">
            <Spinner className="size-3.5" />
            Morgan is revising this draft...
          </div>
        )}

        {/* Subject title (decided drafts). For pending drafts the subject is
            edited inside the compose box below (To / Cc / Subject), where it's
            commonly shown in an email client. */}
        {!pending && (
          <div className="px-6 pb-2">
            <h2 className="font-[Lora,serif] text-xl font-semibold leading-snug text-foreground">
              {draft.subject || "(no subject)"}
            </h2>
          </div>
        )}

        {/* Conversation context — the thread this draft replies to. Full
            bodies (quoted history stripped); the panel itself scrolls. */}
        {draft.threadId && threadData && threadData.messages.length > 0 && (
          <div className="mx-6 mb-3 overflow-hidden rounded-xl border">
            <p className="border-b bg-muted/40 px-4 py-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Conversation
            </p>
            <div className="max-h-[420px] divide-y overflow-y-auto">
              {threadData.messages.map((message) => {
                const isSelf = message.direction === "outbound";
                return (
                  <div key={message._id} className="px-4 py-3">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="truncate text-xs font-semibold text-foreground">
                        {isSelf
                          ? threadData.jobName
                            ? `${threadData.jobName} (mailbox)`
                            : "This mailbox"
                          : message.fromAddress}
                      </span>
                      <span className="shrink-0 text-[11px] text-muted-foreground">
                        {formatDistanceToNow(message.sentAt, {
                          addSuffix: true,
                        })}
                      </span>
                    </div>
                    <p className="mt-1 whitespace-pre-wrap break-words text-xs leading-relaxed text-foreground/80">
                      {splitQuotedText(message.bodyText).main ||
                        (message.bodyTruncated
                          ? "Loading full message..."
                          : "(empty message)")}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Morgan's note */}
        {draft.agentRationale && (
          <div className="mx-6 mb-3 flex gap-2 rounded-lg border border-primary/20 bg-primary/5 px-3.5 py-2.5">
            <Sparkles className="mt-0.5 size-3.5 shrink-0 text-primary" />
            <p className="text-xs leading-relaxed text-foreground/75">
              {draft.agentRationale}
            </p>
          </div>
        )}

        {showDiff && previousBody !== null && (
          <div className="mx-6 mb-3 overflow-hidden rounded-lg border">
            <div className="flex items-center justify-between bg-muted/40 px-3 py-1.5">
              <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                Morgan's changes
              </span>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 text-xs"
                onClick={() => setShowDiff(false)}
              >
                Hide diff
              </Button>
            </div>
            <div className="max-h-64 overflow-y-auto px-3 py-2 font-mono text-xs leading-relaxed">
              {diffLines(previousBody, draft.bodyText).map((part, i) => (
                <span
                  key={i}
                  className={cn(
                    "block whitespace-pre-wrap",
                    part.added &&
                      "bg-emerald-500/10 text-emerald-800 dark:text-emerald-300",
                    part.removed &&
                      "bg-destructive/10 text-destructive line-through decoration-destructive/40",
                  )}
                >
                  {part.value}
                </span>
              ))}
            </div>
          </div>
        )}

        {draft.status === "failed" && draft.error && (
          <div className="mx-6 mb-3 rounded-lg border border-destructive/30 bg-destructive/5 px-3.5 py-2.5">
            <p className="text-xs font-medium text-destructive">
              This draft could not be sent.
            </p>
            <details className="mt-1">
              <summary className="cursor-pointer text-[11px] text-destructive/70 hover:text-destructive">
                Technical details
              </summary>
              <p className="mt-1 max-h-32 overflow-y-auto whitespace-pre-wrap break-all font-mono text-[11px] leading-relaxed text-destructive/80">
                {draft.error}
              </p>
            </details>
          </div>
        )}
        {draft.status === "rejected" && draft.rejectionReason && (
          <p className="mx-6 mb-3 text-xs text-muted-foreground">
            Rejection reason: {draft.rejectionReason}
          </p>
        )}

        {/* Recipients + body */}
        {pending ? (
          <div className="mx-6 mb-6 overflow-hidden rounded-xl border">
            <div className="flex items-start gap-2 border-b px-4 py-2">
              <span className="w-14 shrink-0 pt-1 text-xs font-medium text-muted-foreground">
                To:
              </span>
              <RecipientInput
                value={to}
                onChange={setTo}
                contacts={recipientOptions}
              />
            </div>
            <div className="flex items-start gap-2 border-b px-4 py-2">
              <span className="w-14 shrink-0 pt-1 text-xs font-medium text-muted-foreground">
                Cc:
              </span>
              <RecipientInput
                value={cc}
                onChange={setCc}
                contacts={recipientOptions}
                placeholder="Optional"
              />
              {!showBcc && (
                <button
                  type="button"
                  onClick={() => setBccOpen(true)}
                  className="shrink-0 pt-1 text-xs font-medium text-muted-foreground hover:text-foreground"
                >
                  Bcc
                </button>
              )}
            </div>
            {showBcc && (
              <div className="flex items-start gap-2 border-b px-4 py-2">
                <span className="w-14 shrink-0 pt-1 text-xs font-medium text-muted-foreground">
                  Bcc:
                </span>
                <RecipientInput
                  value={bcc}
                  onChange={setBcc}
                  contacts={recipientOptions}
                  placeholder="Recipients who won't see each other"
                />
              </div>
            )}
            <div className="flex items-center gap-2 border-b px-4 py-2">
              <span className="w-14 shrink-0 text-xs font-medium text-muted-foreground">
                Subject:
              </span>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="min-h-7 flex-1 bg-transparent py-0.5 text-sm outline-none placeholder:text-muted-foreground/60 disabled:cursor-not-allowed"
              />
            </div>
            {richBody ? (
              <NotesEditor
                key={draft._id}
                value={bodyHtml}
                onChange={handleBodyHtmlChange}
                placeholder="Write your email…"
                minHeightClass="min-h-[280px]"
                className="rounded-none border-0 bg-transparent"
              />
            ) : (
              <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={14}
                className="min-h-[280px] resize-y border-0 shadow-none focus-visible:ring-0"
              />
            )}
            <ComposeSignaturePreview jobId={jobId} />
            {/* Action bar — mirrors the new-email composer: actions live at the
                foot of the compose card rather than the reader header. */}
            <div className="flex items-center justify-end gap-2 border-t bg-muted/30 px-3 py-2">
              <span className="mr-auto">
                <MailAttachmentPicker
                  jobId={jobId}
                  attachments={draftAttachments}
                  onChange={setDraftAttachments}
                  disabled={working || revising}
                />
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setReviseOpen(true)}
                disabled={working || revising}
              >
                <Sparkles className="mr-1.5 size-3.5" />
                Revise
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-destructive hover:text-destructive"
                onClick={() => setRejectOpen(true)}
                disabled={working || revising}
              >
                <X className="mr-1.5 size-3.5" />
                Reject
              </Button>
              <Button
                size="sm"
                onClick={handleApprove}
                disabled={working || revising}
              >
                {working ? (
                  <Spinner className="mr-1.5 size-3.5" />
                ) : (
                  <Send className="mr-1.5 size-3.5" />
                )}
                Approve & send
              </Button>
            </div>
          </div>
        ) : (
          <div className="px-6 pb-6">
            <p className="mb-3 text-xs text-muted-foreground">
              to {draft.to.join(", ") || "(no recipients)"}
              {draft.cc.length > 0 && `, cc ${draft.cc.join(", ")}`}
              {(draft.bcc?.length ?? 0) > 0 &&
                `, bcc ${(draft.bcc ?? []).join(", ")}`}
            </p>
            <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground/90">
              {draft.bodyText}
            </p>
          </div>
        )}

        {/* Attachments — below the body, like a normal mail client. The
            generated Day One letter (no PDF → travels in the email body)
            gets its own document card so it is never invisible. */}
        {(Boolean(
          (pending ? draftAttachments : (draft.attachments ?? [])).length,
        ) ||
          Boolean(draft.jobPrecedentId && draft.bodyHtml)) && (
          <div className="border-t px-6 py-4">
            <p className="mb-2 text-xs font-medium text-muted-foreground">
              Attachments
            </p>
            <div className="flex flex-wrap gap-2">
              {draft.jobPrecedentId && draft.bodyHtml && (
                <button
                  type="button"
                  onClick={() =>
                    setPreview({
                      title: draft.subject || "Generated document",
                      html: draft.bodyHtml,
                    })
                  }
                  className="flex items-center gap-2.5 rounded-lg border bg-background px-3 py-2 text-left transition-colors hover:border-primary/40 hover:bg-muted/40"
                >
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/10">
                    <FileText className="size-3.5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="max-w-[240px] truncate text-xs font-medium text-foreground">
                      Generated document
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      Included in the email body · Preview
                    </p>
                  </div>
                </button>
              )}
              {(pending ? draftAttachments : (draft.attachments ?? [])).map(
                (attachment, i) => {
                  const info = infoByStorageId.get(attachment.storageId);
                  const documentId =
                    (attachment.documentId as Id<"documents"> | undefined) ??
                    info?.documentId ??
                    null;
                  // Job documents open the full document detail sheet; other
                  // files (e.g. generated Day One PDFs) get the type-aware
                  // file preview dialog.
                  const handleOpen = () => {
                    if (documentId) {
                      setDetailDocumentId(documentId);
                    } else {
                      setFilePreview({
                        filename: attachment.filename,
                        contentType: attachment.contentType,
                        url: info?.url ?? null,
                      });
                    }
                  };
                  return (
                    <span
                      key={`${attachment.filename}-${i}`}
                      className="relative flex items-center gap-2.5 rounded-lg border bg-background px-3 py-2 text-left transition-colors hover:border-primary/40 hover:bg-muted/40"
                    >
                      <button
                        type="button"
                        onClick={handleOpen}
                        className="flex items-center gap-2.5 text-left"
                      >
                        <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/10">
                          <Paperclip className="size-3.5 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <p className="max-w-[240px] truncate text-xs font-medium text-foreground">
                            {attachment.filename}
                          </p>
                          <p className="text-[11px] text-muted-foreground">
                            {documentId ? "View document" : "Preview"}
                          </p>
                        </div>
                      </button>
                      {pending && (
                        <button
                          type="button"
                          onClick={() =>
                            setDraftAttachments(
                              draftAttachments.filter((_, j) => j !== i),
                            )
                          }
                          aria-label={`Remove ${attachment.filename}`}
                          className="text-muted-foreground hover:text-foreground"
                        >
                          <X className="size-3.5" />
                        </button>
                      )}
                    </span>
                  );
                },
              )}
            </div>
          </div>
        )}

        {showAudit && (
          <div className="border-t px-6 py-4">
            <ApprovalAuditLog draftId={draft._id} />
          </div>
        )}
      </div>

      <AttachmentPreviewModal
        preview={preview}
        onClose={() => setPreview(null)}
      />
      <DocumentDetail
        documentId={detailDocumentId}
        isViewer
        open={detailDocumentId !== null}
        onClose={() => setDetailDocumentId(null)}
      />
      <FilePreviewDialog
        preview={filePreview}
        onClose={() => setFilePreview(null)}
      />
      <RejectDraftDialog
        open={rejectOpen && pending}
        onOpenChange={setRejectOpen}
        onConfirm={(reason) => void handleReject(reason)}
        working={working}
      />
      <ReviseDraftDialog
        open={reviseOpen && pending}
        onOpenChange={setReviseOpen}
        onSubmit={(instructions) => void handleRequestRevision(instructions)}
        working={revising}
      />
    </div>
  );
}
